import React, { useEffect, useRef, useState } from 'react';
import { AccordionSummary, Box } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { AccordionStyle } from '../../styles';
import { SummarySubTitle, SummaryTitle } from './style';
import {
  DefectsAreaForCustomBlocksQuery,
  PaintBlocksCorrodedArea,
  PointOfInterestsFindingsByDeckAndPaintBlock,
} from '@/__generated__/graphql';
import { FindingsGraphs } from './FindingsGraphs';
import { DefectsGraph } from './DefectsGraphs';
import { GraphsLoader } from './GraphsLoader';
import { ConditionSelection } from './ConditionSelection';

type Props = {
  selectedPOIType: string[];
  changePOITypeTab: (argument0: string) => void;
  changeCoatingTab: (argument0: string) => void;
  allCoatingConditions: DefectLegend[] | undefined;
  selectedCoatingCondition: string[];
  graphsLoading: boolean;
  coatingConditionLegend: { [key: string]: string };
  selectedConditionType: string;
  setSelectedConditionType: React.Dispatch<React.SetStateAction<string>>;
  defects: DefectLegend[] | undefined;
  findingsByPaintBlocksAndDecks: PointOfInterestsFindingsByDeckAndPaintBlock | undefined;
  defectAreaForPaintBlocksAndDecks: PaintBlocksCorrodedArea | undefined;
  defectAreaForCustomBlocks?:
    | DefectsAreaForCustomBlocksQuery['defectsAreaForAssemblyPaintRegionsByGroup']
    | undefined;
  activeStructureIds?: string[];
};

type DefectLegend = {
  id: number;
  name: string;
  groupKey: string;
  groupName: string;
};

export const PaintBlockGraph = ({
  coatingConditionLegend,
  selectedPOIType,
  changePOITypeTab,
  changeCoatingTab,
  allCoatingConditions,
  selectedCoatingCondition,
  selectedConditionType,
  setSelectedConditionType,
  graphsLoading,
  defects,
  findingsByPaintBlocksAndDecks,
  defectAreaForPaintBlocksAndDecks,
  defectAreaForCustomBlocks,
  activeStructureIds = [],
}: Props) => {
  const [graphsContainerWidth, setGraphsContainerWidth] = useState<number>(0);
  const graphsContainerReference = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (graphsContainerReference.current) {
      const container = graphsContainerReference.current;
      const resizeObserver = new ResizeObserver(() => {
        setGraphsContainerWidth(container.clientWidth);
      });
      resizeObserver.observe(container);
      return () => {
        resizeObserver.unobserve(container);
      };
    }
    return undefined;
  }, []);

  return (
    <AccordionStyle disableGutters defaultExpanded elevation={0}>
      <AccordionSummary
        sx={{
          padding: '0px !important',
          minHeight: 'fit-content',
        }}
        expandIcon={<ExpandMoreIcon />}
      >
        <SummaryTitle>Summary / charts</SummaryTitle>
      </AccordionSummary>
      <Box ref={graphsContainerReference}>
        {graphsLoading && <GraphsLoader />}
        <ConditionSelection
          defects={defects}
          selectedConditionType={selectedConditionType}
          setSelectedConditionType={setSelectedConditionType}
          selectedCoatingCondition={selectedCoatingCondition}
          changeCoatingTab={changeCoatingTab}
          allCoatingConditions={allCoatingConditions}
        />
        <DefectsGraph
          graphsContainerWidth={graphsContainerWidth}
          defectsAreaForPaintBlockAndDecks={defectAreaForPaintBlocksAndDecks}
          defectAreaForCustomBlocks={defectAreaForCustomBlocks}
          coatingConditionLegend={coatingConditionLegend}
          selectedConditionType={selectedConditionType}
          selectedCoatingCondition={selectedCoatingCondition}
        />
        <SummarySubTitle>Point of Interests</SummarySubTitle>
        <FindingsGraphs
          selectedPOIType={selectedPOIType}
          findingsByPaintBlocksAndDecks={findingsByPaintBlocksAndDecks}
          defectAreaForCustomBlocks={defectAreaForCustomBlocks}
          graphsContainerWidth={graphsContainerWidth}
          changePOITypeTab={changePOITypeTab}
          activeStructureIds={activeStructureIds}
        />
      </Box>
    </AccordionStyle>
  );
};
